import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Divider, Form, Input, List, Space, Switch, Typography, Upload, message } from 'antd';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { MeetingKind, getMeeting, putMeeting, uploadAttendanceSheet } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import { mediaUrl } from '../../utils/media';

const { Text } = Typography;

const SIDES: { key: 'AUDITOR' | 'CONTRACTOR' | 'PROJECT'; label: string }[] = [
  { key: 'AUDITOR', label: 'Auditors' },
  { key: 'CONTRACTOR', label: "Contractor's personnel" },
  { key: 'PROJECT', label: "Project's personnel" },
];

interface Sheet { id: string; url: string; uploaded_at: string; }

interface Props {
  visitId: string;
  kind: MeetingKind;
  editable: boolean;
  active: boolean;
  onChanged: () => void;
}

const COPY: { [k in MeetingKind]: { name: string; presented: string; hint: string } } = {
  opening: {
    name: 'opening meeting',
    presented: 'What the auditors presented (work, scope and method)',
    hint: 'Auditors explain their work and execution methodology in this meeting.',
  },
  closing: {
    name: 'closing meeting',
    presented: 'What the auditors presented (findings)',
    hint: 'The findings are presented and agreed in this meeting.',
  },
};

const MeetingForm: React.FC<Props> = ({ visitId, kind, editable, active, onChanged }) => {
  const [form] = Form.useForm();
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [hasMeeting, setHasMeeting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const held = Form.useWatch('held', form);
  const copy = COPY[kind];

  const load = useCallback(async () => {
    try {
      const res = await getMeeting(visitId, kind);
      const m = res.data;
      setHasMeeting(!!m);
      setSheets(m && m.held ? m.sheets : []);
      const blank = { name: '', designation: '', organisation_name: '' };
      const bySide = (side: string) => {
        const rows = m ? m.attendees.filter((a: any) => a.side === side) : [];
        return rows.length ? rows : [blank];
      };
      form.setFieldsValue({
        held: m ? m.held : true,
        held_on: m && m.held_on ? dayjs(m.held_on) : dayjs(),
        remarks: m ? m.remarks : '',
        not_held_reason: m ? m.not_held_reason : '',
        AUDITOR: bySide('AUDITOR'), CONTRACTOR: bySide('CONTRACTOR'), PROJECT: bySide('PROJECT'),
      });
    } catch (err) {
      setError(getApiError(err, `Could not load the ${copy.name}.`));
    }
  }, [visitId, kind, form, copy.name]);

  useEffect(() => {
    if (active) { setError(''); load(); }
  }, [active, load]);

  const save = async () => {
    const values = await form.validateFields();
    const attendees: any[] = [];
    SIDES.forEach(({ key }) => {
      (values[key] || []).forEach((a: any) => {
        if (a && a.name && a.name.trim()) attendees.push({ side: key, ...a });
      });
    });
    setSaving(true);
    setError('');
    try {
      await putMeeting(visitId, kind, {
        held: values.held !== false,
        held_on: values.held !== false ? values.held_on.format('YYYY-MM-DD') : null,
        remarks: values.remarks || '',
        not_held_reason: values.not_held_reason || '',
        attendees,
      });
      message.success(`${copy.name[0].toUpperCase()}${copy.name.slice(1)} saved.`);
      await load();
      onChanged();
    } catch (err) {
      setError(getApiError(err, `Could not save the ${copy.name}.`));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      await uploadAttendanceSheet(visitId, kind, file);
      message.success('Attendance sheet uploaded.');
      await load();
      onChanged();
    } catch (err) {
      setError(getApiError(err, 'Could not upload the attendance sheet.'));
    } finally {
      setUploading(false);
    }
    return false;
  };

  const wasHeld = held !== false;

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{copy.hint}</Text>

      <Form form={form} layout="vertical" disabled={!editable}>
        {kind === 'opening' && (
          <Form.Item name="held" label="Was the opening meeting held?" valuePropName="checked" initialValue>
            <Switch checkedChildren="Held" unCheckedChildren="Not held" />
          </Form.Item>
        )}

        {!wasHeld && (
          <Form.Item
            name="not_held_reason" label="Reason why it was not held"
            rules={[{ required: true, message: 'Give the reason' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        )}

        {wasHeld && (
          <>
            <Space align="start" size="large" wrap>
              <Form.Item name="held_on" label="Meeting held on" rules={[{ required: true, message: 'Enter the date' }]}>
                <DatePicker />
              </Form.Item>
              <Form.Item name="remarks" label={copy.presented} style={{ minWidth: 420 }}>
                <Input.TextArea rows={4} />
              </Form.Item>
            </Space>

            {SIDES.map(({ key, label }) => (
              <div key={key}>
                <Divider titlePlacement="start" plain>{label} present (at least one)</Divider>
                <Form.List name={key}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => (
                        <Space key={field.key} align="baseline" style={{ display: 'flex' }} wrap>
                          <Form.Item name={[field.name, 'name']}><Input placeholder="Name" style={{ width: 190 }} /></Form.Item>
                          <Form.Item name={[field.name, 'designation']}><Input placeholder="Designation" style={{ width: 150 }} /></Form.Item>
                          <Form.Item name={[field.name, 'organisation_name']}><Input placeholder="Organisation" style={{ width: 190 }} /></Form.Item>
                          {editable && <Button type="text" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />}
                        </Space>
                      ))}
                      {editable && (
                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', designation: '', organisation_name: '' })}>
                          Add person
                        </Button>
                      )}
                    </>
                  )}
                </Form.List>
              </div>
            ))}
          </>
        )}
      </Form>

      {editable && (
        <Button type="primary" onClick={save} loading={saving} style={{ marginTop: 16 }}>
          Save {copy.name}
        </Button>
      )}

      {wasHeld && (
        <>
          <Divider titlePlacement="start" plain>Signed attendance sheet (mandatory)</Divider>
          {!hasMeeting && <Text type="secondary">Save the {copy.name} first, then attach the signed sheet.</Text>}
          {hasMeeting && (
            <>
              <List
                size="small"
                locale={{ emptyText: 'No sheet attached yet' }}
                dataSource={sheets}
                renderItem={(s) => (
                  <List.Item>
                    <a href={mediaUrl(s.url)} target="_blank" rel="noreferrer">Attendance sheet</a>
                    <Text type="secondary">{dayjs(s.uploaded_at).format('DD MMM YYYY HH:mm')}</Text>
                  </List.Item>
                )}
              />
              {editable && (
                <Upload accept=".pdf,.jpg,.jpeg,.png" showUploadList={false} beforeUpload={upload}>
                  <Button icon={<UploadOutlined />} loading={uploading} style={{ marginTop: 8 }}>
                    Upload sheet (PDF, JPG or PNG)
                  </Button>
                </Upload>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MeetingForm;
